import { Badge, Button } from 'react-daisyui';
import { MarketplaceVendorsDTO } from '@shared/types/marketplace';
import { useState } from 'react';
import GenericConfirmationModal from '@/components/Modal/GenericConfirmationModal';
import { post } from '@/utils/queries';
import { alertErrorMessage } from '@/utils/alerts';
import { getVendorCategoryColor } from '@/pages/Marketplace/utils';
import { IUserContext, useUserContext } from '@/context/UserContext';
import { DateTime } from 'luxon';

const version = DateTime.now().toFormat('yyyyMMdd');

export function MarketplaceVendorItem({
  vendorItemData,
}: {
  vendorItemData: MarketplaceVendorsDTO;
}) {
  const { auth }: IUserContext = useUserContext();
  const [showModal, setShowModal] = useState(false);
  const handleClick = () => {
    if (vendorItemData.partnership) {
      setShowModal(true);
    } else {
      window.open(vendorItemData.url, '_blank');
    }
  };

  const contactOpstartTeam = async () => {
    try {
      await post(
        `marketplaceVendors/message/${vendorItemData.id}/client/${
          auth?.clients[0].id as number
        }`,
        {}
      );
      setShowModal(false);
    } catch {
      alertErrorMessage('Something went wrong, try again later.');
    }
  };

  return (
    <>
      {showModal ? (
        <GenericConfirmationModal
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onAcceptAction={() => contactOpstartTeam()}
          onCloseAction={() => setShowModal(false)}
          showModal={!!showModal}
          modalMessage={`Press confirm to send an email to our team to learn more about our partnership
             and onboarding process with the selected vendor (${vendorItemData.name}).`}
        />
      ) : null}
      <div
        key={`card-${vendorItemData.name}`}
        className='my-2 mx-2 shadow card rounded-2xl w-[32%] bg-gradient-to-br from-[#f5f7fa] to-[#c3cfe2] border
        border-gray-300 flex flex-col justify-between p-4 text-gray-900 hover:shadow-lg transition-shadow duration-200'
      >
        <div className='flex items-center mb-2'>
          <img
            src={`${vendorItemData.logo_src}?v=${version}`}
            alt={vendorItemData.name}
            className='rounded mr-3 max-w-[3.5rem] max-h-[3.5rem]'
          />
          <div className='grid'>
            <span className='font-bold text-lg'>{vendorItemData.name}</span>
            {vendorItemData.recommended && (
              <Badge color='warning' size='sm' className=''>
                <span className='text-black'>Recommended</span>
              </Badge>
            )}
          </div>
        </div>
        <div className='mb-2 text-sm text-gray-700'>
          {vendorItemData.description}
        </div>
        <div className='flex flex-wrap gap-2 mb-2'>
          {vendorItemData.category.map((cat) => (
            <Badge
              color='primary'
              size='sm'
              variant='outline'
              key={`category_${cat}_${vendorItemData.name}`}
              style={{
                color: getVendorCategoryColor(cat) ?? 'inherit',
              }}
            >
              <span className='font-bold'>{cat}</span>
            </Badge>
          ))}
        </div>
        <div className='flex justify-between items-center mt-2'>
          <div className='flex items-center w-[33%]'>
            {vendorItemData.partnership ? (
              <Badge
                color='accent'
                size='lg'
                variant='outline'
                key={`partner_${vendorItemData.name}`}
                className='h-8'
              >
                <img
                  src='../opstart-new-logo.png'
                  alt='opstart-logo'
                  className='w-6'
                />
                <span className='text-xs pl-1'> Partner</span>
              </Badge>
            ) : null}
          </div>
          <div className='w-[33%]'>
            <Button
              color='accent'
              id='upload'
              data-testid='visit-preview-button'
              size='sm'
              variant='outline'
              className={`normal-case ${
                !auth?.isClient ? 'opacity-50 pointer-events-none' : ''
              }`}
              disabled={!vendorItemData.url}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={() => handleClick()}
            >
              Learn More
            </Button>
          </div>
          <span className='font-semibold text-xs w-[33%] text-right'>
            {/* ${vendorItemData.price} */}
          </span>
        </div>
      </div>
    </>
  );
}
